// The one rule of every CRUD write (`logged-write.ts`), with no React: the save first, then its
// System log entry — only once the save answered 2xx. A save that throws (the platform's client
// throws on every non-2xx: a 400, a 409, a 503) writes no entry and rethrows, so the caller reports
// the failure (the red message, "M failed"). `scripts/routing-checks.ts` checks it with the real
// client over a mocked fetch.

import { crudInput } from "@app/shared";
import type { ChangeSpec, CrudEntity } from "@app/shared";

export type Rec = Record<string, unknown> & { id: string; rv?: string };

/**
 * A write's entry: its own, or that of the action it is part of (named, for the reader); or none,
 * for the person's own setting (what it is, named).
 */
export type Log = ChangeSpec | { partOf: string } | { own: string };

export interface LoggedWrites {
  create(entity: CrudEntity, input: Record<string, unknown>, log: Log): Promise<Rec>;
  update(entity: CrudEntity, id: string, rv: string, input: Record<string, unknown>, log: Log): Promise<Rec>;
  remove(entity: CrudEntity, id: string, rv: string, log: Log): Promise<void>;
  /** A decision that changes no record (a release review): its System log entry alone. */
  note(entity: CrudEntity, recordId: string, log: ChangeSpec): Promise<void>;
}

/** The platform's CRUD client, as the writes call it (`crudApi`). */
export interface CrudCalls {
  create(entity: string, input: Record<string, unknown>): Promise<Rec>;
  update(entity: string, id: string, rv: string, input: Record<string, unknown>): Promise<Rec>;
  remove(entity: string, id: string, rv: string): Promise<void>;
}

/** Every write through `crud`, then its entry through `entry` — never before, never after a failed save. */
export function loggedWriter(
  crud: CrudCalls,
  entry: (entity: CrudEntity, recordId: string, log: Log) => Promise<void>,
): LoggedWrites {
  return {
    async create(entity, input, log) {
      // As the API takes it (`crudInput`): every field, a text never null — whatever the caller built.
      const saved = await crud.create(entity.id, crudInput(entity, input));
      await entry(entity, saved.id, log);
      return saved;
    },
    async update(entity, id, rv, input, log) {
      const saved = await crud.update(entity.id, id, rv, crudInput(entity, input));
      await entry(entity, id, log);
      return saved;
    },
    async remove(entity, id, rv, log) {
      await crud.remove(entity.id, id, rv);
      await entry(entity, id, log);
    },
    async note(entity, recordId, log) {
      await entry(entity, recordId, log);
    },
  };
}
